/**
 * DYN-4 Phase 2b — AutoCAD 2018+ dimensional constraint parameter parsers.
 *
 * Context (observation from АБИ1.dxf "Двутавр ГОСТ 8239-89" and ezdxf):
 * ────────────────────────────────────────────────────────────────────
 * AutoCAD 2018+ emits parametric-block dimensional constraints with
 * DIFFERENT DXF group codes than the Batch-C reverse-engineered ones
 * wired into `dxfBridge.dynamic.parser.tier2.ts`. The existing tier2
 * parser reads `10/20` (point A), `11/21` (point B), `140` as
 * `defaultDistance`, `141` as `currentDistance`, and `1` as the name.
 * Real АБИ1.dxf, matching ezdxf's reference decoding, uses:
 *
 *   300         → parameter name (e.g. "b", "s", "h", "L1", "угол1")
 *   305         → parameter name (duplicate after AcDbBlockLinearConstraintParameter)
 *   1071        → flag (always 0 in observed files)
 *   1010/1020/1030 → base point (X / Y / Z) — inherited from AcDbBlock2PtParameter
 *   1011/1021/1031 → second point (X / Y / Z)
 *   1012/1022/1032 → angular-only third point (vertex override emitted AFTER
 *                    the AcDbBlockAngularConstraintParameter subclass marker)
 *   140         → current value (signed):
 *                 - Horizontal/Vertical/Aligned: current distance (signed;
 *                   abs() for display)
 *                 - Radial:                      current radius
 *                 - Angular:                     current angle (radians)
 *   141         → min / list-scoped value (part of the value set encoding)
 *   142         → max (part of the value set)
 *   143         → increment (part of the value set)
 *   96          → value-set row count
 *   175         → value-set mode flag
 *
 * This module parses the OBSERVED encoding. It DOES NOT replace the
 * existing Batch-C parsers — those remain authoritative for the
 * synthesized group-code-10/20 shape used in round-trip unit tests. The
 * canonical-class dispatcher (Phase 3) will prefer this module when a
 * constraint object is encountered that carries the 1010/1020 BPT codes;
 * it will fall back to the tier2 parser otherwise.
 *
 * All parsers here are tolerant to missing fields and fall back to the
 * tier2 code convention (`10/20`, `11/21`, `1`) when the 1010/1020 / 300
 * codes are absent, so an object synthesized with the older shape still
 * decodes correctly without re-routing.
 *
 * ezdxf reference:
 *   https://ezdxf.mozman.at/docs/dxfentities/acad_block_parameter.html
 */
import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type {
  AngularConstraintParameter,
  HorizontalConstraintParameter,
  RadialConstraintParameter,
  VerticalConstraintParameter,
} from "./dynamicBlock";
import {
  firstGroup,
  firstInt,
  firstNumber,
} from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Value-dep handle extraction
// ═══════════════════════════════════════════════════════════════════════════
//
// Every dimensional constraint parameter emits its value-dep back-reference
// as the FIRST group 330 directly after the `AcDbBlockConstraintParameter`
// subclass marker. The marker shape is:
//
//   100  AcDbBlockConstraintParameter
//   330  <valueDepHandle>              ← we want this
//   100  AcDbBlockLinearConstraintParameter  (or AcDbBlockAngular… etc.)
//
// We walk the raw groups, flip a "past marker" bit on the 100 marker,
// and capture the first 330 that follows. Returns "" when the marker is
// absent (round-trip-only synthetic objects have no value-dep).
export function extractValueDepHandle(groups: DxfGroup[]): string {
  let pastMarker = false;
  for (const g of groups) {
    if (g.code === 100) {
      if (g.value === "AcDbBlockConstraintParameter") {
        pastMarker = true;
      } else if (pastMarker) {
        // Any subsequent 100 (subclass marker for Linear / Angular / …)
        // closes the window — the value-dep must appear before it.
        return "";
      }
    } else if (g.code === 330 && pastMarker) {
      return g.value.trim();
    }
  }
  return "";
}

// ═══════════════════════════════════════════════════════════════════════════
// Internal helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Read a point from AutoCAD's BPT (Block-Parameter-Point) group codes
 * (typically `1010/1020` for basePoint, `1011/1021` for secondPoint, etc.),
 * falling back to the legacy tier2 codes (10/20, 11/21) when the modern
 * 1010/1020 codes are absent. Lets the new parser survive on both AutoCAD
 * 2018+ files and our own round-trip writer output.
 *
 * The Z axis is discarded — our `drawingPointSchema` is 2D — but the
 * writer still emits a matching `1030 / 1031 / 30 / 31` so round-trip
 * fidelity of the raw group stream is preserved by `rawGroups`.
 */
function pointWithLegacyFallback(
  groups: DxfGroup[],
  bptX: number,
  bptY: number,
  legacyX: number,
  legacyY: number,
): { x: number; y: number } {
  const x = firstNumber(groups, bptX) ?? firstNumber(groups, legacyX) ?? 0;
  const y = firstNumber(groups, bptY) ?? firstNumber(groups, legacyY) ?? 0;
  return { x, y };
}

/**
 * Resolve the parameter name. AutoCAD 2018+ stores it at group 300;
 * group 305 repeats it after the `AcDbBlockLinearConstraintParameter`
 * (or `AcDbBlockAngularConstraintParameter`, etc.) subclass marker.
 * Legacy Batch-C shape stores the name at group 1. When all three are
 * missing we synthesise `<prefix>_<handle>` to keep the id slot non-empty.
 */
function resolveConstraintName(
  groups: DxfGroup[],
  handle: string,
  fallbackPrefix: string,
): string {
  const code300 = firstGroup(groups, 300)?.trim();
  if (code300 && code300.length > 0) return code300;
  const code305 = firstGroup(groups, 305)?.trim();
  if (code305 && code305.length > 0) return code305;
  const code1 = firstGroup(groups, 1)?.trim();
  if (code1 && code1.length > 0) return code1;
  return `${fallbackPrefix}_${handle}`;
}

/**
 * Decode a value-set axis from the observed encoding (modeCode 175,
 * increment/min/max at 143/141/142, list at 145 per observed AcDb2PtParameter
 * + ConstraintParameter chunk). Returns a conservative `{ mode: "none" }`
 * value-set when the codes are all absent — the caller can still round-
 * trip because rawGroups preserves the full object.
 */
function parseConstraintValueSet(obj: DxfDynamicBlockObject) {
  const groups = obj.rawGroups;
  const modeRaw = firstInt(groups, 175) ?? firstInt(groups, 170) ?? 0;
  const mode: "none" | "increment" | "list" =
    modeRaw === 1 ? "increment" : modeRaw === 2 ? "list" : "none";
  const increment = firstNumber(groups, 143) ?? firstNumber(groups, 142) ?? 0;
  const min = firstNumber(groups, 141);
  const max = firstNumber(groups, 142);
  // Batch-C synthetic round-trip fixtures emit list values under code 145
  // (matches emitAxisValueSet in dxfBridge.dynamic.writer.tier2.ts). Collect
  // them for round-trip even when АБИ1.dxf rarely uses list-mode for
  // constraint params.
  const listValuesRaw = groups
    .filter((g) => g.code === 145)
    .map((g) => Number.parseFloat(g.value));
  const listValues = listValuesRaw.filter(Number.isFinite);
  const valueSet: {
    mode: "none" | "increment" | "list";
    increment: number;
    listValues: number[];
    min?: number;
    max?: number;
  } = {
    mode,
    increment,
    listValues,
  };
  // AutoCAD's constraint-parameter value set uses 141 as min and 142 as max
  // (verified against АБИ1.dxf). Only include when we actually read them and
  // they are not the "sentinel zero" placeholder AutoCAD writes when no
  // value set is active (mode 0).
  if (min !== undefined && mode !== "none") valueSet.min = min;
  if (max !== undefined && mode !== "none") valueSet.max = max;
  return valueSet;
}

// ═══════════════════════════════════════════════════════════════════════════
// Horizontal / Vertical constraint parameters
// ═══════════════════════════════════════════════════════════════════════════
//
// Shape (observed): pointA at 1010/1020 (BPT) + pointB at 1011/1021 (BPT);
// the current distance sits at code 140, SIGNED — АБИ1.dxf writes negative
// numbers (the sign encodes grip-drag direction; AutoCAD's Property Palette
// displays the absolute value). `defaultDistance` copies `currentDistance`
// when no separate 141/default is available; this matches the linear /
// aligned convention elsewhere in the codebase.

export function parseHorizontalConstraintParameterFromBpt(
  obj: DxfDynamicBlockObject,
): HorizontalConstraintParameter {
  const groups = obj.rawGroups;
  const pointA = pointWithLegacyFallback(groups, 1010, 1020, 10, 20);
  const pointB = pointWithLegacyFallback(groups, 1011, 1021, 11, 21);
  const rawDistance = firstNumber(groups, 140) ?? 0;
  const currentDistance = Math.abs(rawDistance);
  const defaultDistance =
    firstNumber(groups, 141) !== undefined
      ? Math.abs(firstNumber(groups, 141) as number)
      : currentDistance;
  return {
    kind: "constraint-horizontal",
    id: obj.handle,
    name: resolveConstraintName(groups, obj.handle, "constraint_horizontal"),
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    pointA,
    pointB,
    defaultDistance,
    currentDistance,
    distanceValueSet: parseConstraintValueSet(obj),
    // Explicit entity refs are populated by the bridge layer's post-pass
    // (`enrichConstraintParamsWithAssocEntities`) after parsing. Default
    // to [] so schema-compatibility is maintained when the post-pass
    // cannot resolve any assoc deps.
    associatedEntities: [],
  };
}

export function parseVerticalConstraintParameterFromBpt(
  obj: DxfDynamicBlockObject,
): VerticalConstraintParameter {
  const groups = obj.rawGroups;
  const pointA = pointWithLegacyFallback(groups, 1010, 1020, 10, 20);
  const pointB = pointWithLegacyFallback(groups, 1011, 1021, 11, 21);
  const rawDistance = firstNumber(groups, 140) ?? 0;
  const currentDistance = Math.abs(rawDistance);
  const defaultDistance =
    firstNumber(groups, 141) !== undefined
      ? Math.abs(firstNumber(groups, 141) as number)
      : currentDistance;
  return {
    kind: "constraint-vertical",
    id: obj.handle,
    name: resolveConstraintName(groups, obj.handle, "constraint_vertical"),
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    pointA,
    pointB,
    defaultDistance,
    currentDistance,
    distanceValueSet: parseConstraintValueSet(obj),
    associatedEntities: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Radial constraint parameter
// ═══════════════════════════════════════════════════════════════════════════
//
// АБИ1.dxf class name: BLOCKRADIALCONSTRAINTPARAMETER (canonical
// ACDBBLOCKRADIALCONSTRAINTPARAMETER). NOT the same as the tier3
// ACDBBLOCKCONSTRAINEDRADIAL class — that one is a separate "Radial dim.
// constraint" entity from an older AutoCAD vintage. We map both spellings
// onto the same `RadialConstraintParameter` schema since they carry
// semantically identical information.
//
// Encoding:
//   1010/1020 → center (inherited via AcDbBlock2PtParameter)
//   1011/1021 → reference point on the circle ("radiusPoint" in our schema).
//               АБИ1.dxf emits the reference point even though AutoCAD's
//               solver needs only the scalar radius; this ensures the grip
//               draws at the same place on re-open.
//   140       → current radius (always positive in observed files)

export function parseRadialConstraintParameterFromBpt(
  obj: DxfDynamicBlockObject,
): RadialConstraintParameter {
  const groups = obj.rawGroups;
  const center = pointWithLegacyFallback(groups, 1010, 1020, 10, 20);
  const radiusPoint = pointWithLegacyFallback(groups, 1011, 1021, 11, 21);
  const currentRadius = Math.abs(firstNumber(groups, 140) ?? 0);
  const defaultRadius =
    firstNumber(groups, 141) !== undefined
      ? Math.abs(firstNumber(groups, 141) as number)
      : currentRadius;
  return {
    kind: "constraint-radial",
    id: obj.handle,
    name: resolveConstraintName(groups, obj.handle, "constraint_radial"),
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    center,
    radiusPoint,
    defaultRadius,
    currentRadius,
    radiusValueSet: parseConstraintValueSet(obj),
    associatedEntities: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Angular constraint parameter
// ═══════════════════════════════════════════════════════════════════════════
//
// АБИ1.dxf angular parameter storage is more complex than distance: it
// emits TWO point pairs. The FIRST pair (1010/1020 base + 1011/1021 second)
// comes from the inherited AcDbBlock2PtParameter subclass and describes the
// "seed" ray the UI grip dragged from. The SECOND pair, emitted AFTER the
// `AcDbBlockAngularConstraintParameter` subclass marker, uses codes
// 1011/1021/1031 + 1012/1022/1032 — we treat these as the `firstEndpoint`
// and `secondEndpoint` that define the angle arms.
//
// Current angle sits at code 140 (radians per AutoCAD's DXF convention).
// The АБИ1.dxf file has the value 6.283185307179586 in the value set (2π
// upper bound), so 140 itself is the measured angle.

export function parseAngularConstraintParameterFromBpt(
  obj: DxfDynamicBlockObject,
): AngularConstraintParameter {
  const groups = obj.rawGroups;
  // The inherited 2PtParameter supplies the vertex at 1010/1020. For legacy
  // round-trip objects the vertex may have been written at code 12/22
  // (Batch-C parsing); honour that too.
  const vertex = pointWithLegacyFallback(groups, 1010, 1020, 12, 22);
  // The angular-specific subclass block emits 1011/1021 (first endpoint)
  // and 1012/1022 (second endpoint) — note that 1011/1021 is also where the
  // inherited 2PtParameter stores its "second point"; AutoCAD overwrites it
  // with the first ray endpoint when it re-serialises, so reading 1011/1021
  // after the subclass marker lands on the correct value.
  // `collectGroups` would technically let us pick the SECOND occurrence of
  // 1011/1021, but both occurrences carry the same value per АБИ1.dxf
  // observation, so `firstNumber` is sufficient.
  const pointA = pointWithLegacyFallback(groups, 1011, 1021, 10, 20);
  const pointB = pointWithLegacyFallback(groups, 1012, 1022, 11, 21);
  const currentAngle = firstNumber(groups, 140) ?? 0;
  const defaultAngle = firstNumber(groups, 141) ?? currentAngle;
  return {
    kind: "constraint-angular",
    id: obj.handle,
    name: resolveConstraintName(groups, obj.handle, "constraint_angular"),
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    vertex,
    pointA,
    pointB,
    defaultAngle,
    currentAngle,
    angleValueSet: parseConstraintValueSet(obj),
    associatedEntities: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Dispatch
// ═══════════════════════════════════════════════════════════════════════════
//
// Set of canonical (post-`canonicalDynamicBlockClass`) class names that the
// top-level parser should route to this module. The tier2 / tier3
// dispatchers handle the older synthesized shape; this set covers the
// AutoCAD 2018+ un-prefixed spellings observed in АБИ1.dxf after
// canonicalisation.
export const CONSTRAINT_PARAMETER_CLASSES = new Set<string>([
  "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER",
  "ACDBBLOCKVERTICALCONSTRAINTPARAMETER",
  "ACDBBLOCKRADIALCONSTRAINTPARAMETER",
  "ACDBBLOCKANGULARCONSTRAINTPARAMETER",
]);

/**
 * Resolve the matching BPT-form parser for a canonical class name. Returns
 * `undefined` when the class is not a constraint parameter, so the caller
 * can fall through to tier2 / tier3 dispatch.
 */
export function tryParseConstraintParameterFromBpt(
  obj: DxfDynamicBlockObject,
  canonicalType: string,
):
  | HorizontalConstraintParameter
  | VerticalConstraintParameter
  | RadialConstraintParameter
  | AngularConstraintParameter
  | undefined {
  switch (canonicalType) {
    case "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER":
      return parseHorizontalConstraintParameterFromBpt(obj);
    case "ACDBBLOCKVERTICALCONSTRAINTPARAMETER":
      return parseVerticalConstraintParameterFromBpt(obj);
    case "ACDBBLOCKRADIALCONSTRAINTPARAMETER":
      return parseRadialConstraintParameterFromBpt(obj);
    case "ACDBBLOCKANGULARCONSTRAINTPARAMETER":
      return parseAngularConstraintParameterFromBpt(obj);
    default:
      return undefined;
  }
}
